// The one way a screen loads from the API (Step 26): load, show a plain
// error with "Try again" when it fails, and load again on the click. Every
// failure ends in the error state, so a page never stays on "Loading…" and
// no rejection goes unhandled. New screens use this by default.
import { useCallback, useEffect, useRef, useState } from 'react';

export type LoadState<T> =
  | { status: 'loading' }
  | { status: 'loaded'; value: T; reload: () => void }
  | { status: 'failed'; retry: () => void };

// `key` names what is loaded (for example the slug); a new key loads again.
export function useLoad<T>(load: () => Promise<T>, key = ''): LoadState<T> {
  const [state, setState] = useState<{ status: 'loading' | 'failed' } | { value: T }>({
    status: 'loading',
  });
  // The latest load function, so a retry uses the current inputs.
  const latest = useRef(load);
  latest.current = load;

  const run = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      setState({ value: await latest.current() });
    } catch {
      // Any failure, an unreachable API or not, is shown to the person as
      // the error with Try again. Its details never reach the page.
      setState({ status: 'failed' });
    }
  }, []);

  useEffect(() => {
    void run();
  }, [run, key]);

  const again = () => void run();
  if ('value' in state) return { status: 'loaded', value: state.value, reload: again };
  return state.status === 'failed' ? { status: 'failed', retry: again } : { status: 'loading' };
}

// A failed action (save, join): the same plain message and "Try again".
// Test IDs: <testId>-error and <testId>-retry (Step 27).
export function ActionError(props: { testId: string; message: string; onRetry: () => void }) {
  return (
    <div className="load-error">
      <p role="alert" data-testid={`${props.testId}-error`}>
        {props.message}
      </p>
      <button type="button" data-testid={`${props.testId}-retry`} onClick={props.onRetry}>
        Try again
      </button>
    </div>
  );
}

// The error and its "Try again" button. Test IDs: <testId>-load-error and
// <testId>-retry.
export function LoadError(props: { testId: string; message: string; onRetry: () => void }) {
  return (
    <div className="load-error">
      <p role="alert" data-testid={`${props.testId}-load-error`}>
        {props.message}
      </p>
      <button type="button" data-testid={`${props.testId}-retry`} onClick={props.onRetry}>
        Try again
      </button>
    </div>
  );
}
